import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import clsx from 'clsx';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import { GrMap } from 'react-icons/gr';
import { GrMapLocation } from 'react-icons/gr';
import { getService } from 'reducers/service';
import { handleError } from 'reducers/ErrorReducer';
import { IconButton } from '@material-ui/core';
import { AddIcon } from 'evergreen-ui';
import { showAddressDialog } from 'reducers/PersonDialogsReducer';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';

const btnStyle = {
  margin: '0 8px 0 2px',
  padding: 0,
};

const chevronStyle = {
  position: 'absolute',
  right: -5,
  top: 0,
  display: 'block',
  width: 20,
  height: 20,
  borderRadius: '50%',
  textAlign: 'center',
  fontSize: 14,
  cursor: 'pointer',
  zIndex: 100,
  opacity: 0.3,
};

const useStyles = makeStyles((theme) => ({
  wrap: {
    position: 'relative',
    '& .MuiAutocomplete-clearIndicator': {
      visibility: 'visible',
      opacity: 0.2,
      transition: 'all 0.1s',
    },
    '&:hover, & .Mui-focused': {
      '& $bulletinInfo': {
        opacity: 1,
      },
      '& .MuiAutocomplete-clearIndicator': {
        opacity: 1,
      },
    },
  },
  icon: {
    marginRight: theme.spacing(2),
  },
  bulletinInfo: {
    position: 'absolute',
    right: 60,
    top: 5,
    opacity: 0.2,
    transition: 'all 0.1s',
  },
  iconButton: {
    color: theme.palette.error.main,
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: theme.typography.fontWeightRegular,
  },
  option: {
    position: 'relative',
    overflow: 'hidden',
    '& div': {
      fontSize: '0.9em',
    },
  },
}));

export const getPlace = (ptsPlaceID) => {
  const service = getService('cad');
  return service.get({ type: 'get-place', data: { ptsPlaceID } });
};

function AddressLookup(props) {
  const {
    wsClient,
    ptsPlaces,
    ptsPlaceID,
    ptsAddresses,
    map,
    onReset,
    setPlace,
    label = 'search',
    error,
    compact,
    dataUpdate,
    dialogs,
    inputRef,
    ptsAddressID,
  } = props;

  const classes = useStyles();
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [autocompleteOptions, setAutocompleteOptions] = useState([]); // list of search results
  const [extAddressUpdated, setExtAddressUpdated] = useState(false);
  const [address, setAddress] = useState(null);
  const activeRef = useRef(false);
  const throttleRef2 = useRef(0);
  const addEnabled = dialogs.address === null;

  useEffect(() => {
    if (ptsAddressID) updateCurrentAddress();
    // eslint-disable-next-line
  }, [ptsAddressID]);

  useEffect(() => {
    clearTimeout(throttleRef2.current);
    throttleRef2.current = setTimeout(() => {
      if (inputValue.length > 2) {
        search();
      } else {
        setAutocompleteOptions([]);
      }
    }, 500);
    // eslint-disable-next-line
  }, [inputValue]);

  useEffect(() => {
    if (!dataUpdate || dataUpdate.type !== 'Address') return;
    handlePlaceSet(dataUpdate.data);
    // eslint-disable-next-line
  }, [dataUpdate]);

  useEffect(() => {
    handlePlaceSet();
    // eslint-disable-next-line
  }, [setPlace]);

  useEffect(() => {
    if (!ptsPlaceID) return;
    setPlaceByID(ptsPlaceID);
    // eslint-disable-next-line
  }, [ptsPlaceID]);

  useEffect(() => {
    activeRef.current = true;
    if (inputValue === '') return setAutocompleteOptions(value ? [value] : []);
    return () => {
      activeRef.current = false;
    };
    // eslint-disable-next-line
  }, [value, inputValue, wsClient.websocket, ptsAddresses, map]);

  const latestInputRef = useRef('');

  useEffect(() => {
    latestInputRef.current = inputValue;
  }, [inputValue]);

  const updateCurrentAddress = async () => {
    const address = await getAddress(ptsAddressID);
    if (!address) return;
    handlePlaceSet(address);
  };

  const search = () => {
    if (!inputValue) {
      setAutocompleteOptions([]);
      return;
    }
    findAddress(inputValue);
  };

  const setPlaceByID = async (ptsPlaceID) => {
    try {
      const place = await getPlace(ptsPlaceID);
      handlePlaceSet(place);
    } catch (err) {
      props.handleError(err);
    }
  };

  const findAddress = async (searchTerm) => {
    if (ptsAddresses && ptsPlaces && extAddressUpdated) return setExtAddressUpdated(false);
    try {
      const service = getService('address-lookup');
      let addresses = await service.find({ query: { FullAddressText: searchTerm } });
      if (searchTerm !== latestInputRef.current) {
        return; // Ignore stale response
      }
      if (addresses && addresses.length > 0) {
        sortAddresses(addresses);
        setAutocompleteOptions(addresses);
      } else {
        setAutocompleteOptions([]);
        setValue(null);
      }
    } catch (error) {
      props.handleError(error);
    }
  };

  function sortAddresses(addresses) {
    // sort by address number
    addresses.sort((a, b) => {
      const aSplit = splitAddressByAddressNo(a.FullAddressText);
      const bSplit = splitAddressByAddressNo(b.FullAddressText);
      const aNumber = aSplit.number;
      const bNumber = bSplit.number;
      if (aNumber === bNumber || isNaN(aNumber) || isNaN(bNumber)) {
        return 0;
      }
      const intA = parseInt(aNumber, 10);
      const intB = parseInt(bNumber, 10);
      return intA < intB ? -1 : 1;
    });

    // sort by remaining text
    addresses.sort((a, b) => {
      const aSplit = splitAddressByAddressNo(a.FullAddressText);
      const bSplit = splitAddressByAddressNo(b.FullAddressText);
      const aRemaining = aSplit.remaining;
      const bRemaining = bSplit.remaining;

      if (aRemaining === bRemaining) {
        return 0;
      }
      return aRemaining < bRemaining ? -1 : 1;
    });
  }

  function splitAddressByAddressNo(address) {
    const number = address.match(/^\d+/);
    if (number) {
      if (address.match(/^\d+ [A-Z] /)) {
        const remaining = address.replace(/^\d+ [A-Z] /, '').trim();
        return { number: number[0], remaining };
      }
      return { number: number[0], remaining: address.replace(number[0], '').trim() };
    }
    return { number: null, remaining: address };
  }

  // If place set by parent component
  const handlePlaceSet = (place = setPlace) => {
    if (!place) {
      if (value || inputValue) {
        setAutocompleteOptions([]);
        setValue(null);
        setExtAddressUpdated(true);
      }
      return;
    } else if (!place.ptsAddressID) return;
    const autocompleteOptions = [{ ...place }];
    setAutocompleteOptions(autocompleteOptions);
    setValue(autocompleteOptions[0]);
    setExtAddressUpdated(true);
    if (place !== setPlace) {
      props.onAddressValueSet(place);
    }
  };

  const getOptionLabel = (option) => {
    if (option.FullAddressText) {
      if (option.PlaceName) {
        return `${option.PlaceName} - ${option.FullAddressText}`;
      } else {
        return option.FullAddressText;
      }
    }
  };

  const onAddressSelect = async (ev, newValue) => {
    setAutocompleteOptions(newValue ? [newValue, ...autocompleteOptions] : autocompleteOptions);
    setValue(newValue);
    if (!newValue) return onReset && onReset();
    const addressDetails = await getAddress(newValue.ptsAddressID);
    const address = { ...newValue, ...addressDetails };
    setAddress(address);
    props.onAddressValueSet && props.onAddressValueSet(address);
  };

  const getAddress = async (ptsAddressID) => {
    let address = null;
    const service = getService('address-lookup');
    try {
      address = await service.get(ptsAddressID);
    } catch (err) {
      props.handleError(err);
    }
    return address;
  };

  const onInputChange = (ev, newInputValue, reason) => {
    if (reason === 'input') setInputValue(newInputValue);
  };

  const renderOptionIcon = (option) => {
    if (option.PlaceName === null) {
      return (
        <Grid item>
          <GrMap className={classes.icon} style={{ color: 'green', fontSize: '24px' }} />
        </Grid>
      );
    } else {
      return (
        <Grid item>
          <GrMapLocation className={classes.icon} style={{ color: 'red', fontSize: '24px' }} />
        </Grid>
      );
    }
  };

  const onOptionExpand = (ev) => {
    const isSVGClicked = ev.target.parentElement.style.opacity === '0.3';
    const el = isSVGClicked ? ev.target.parentElement : ev.target;
    const parent = el.parentElement;
    const isCollapsed = parent.style.maxHeight === '43px';
    if (isCollapsed) {
      parent.style.maxHeight = '100%';
      el.style.transform = 'rotate(180deg)';
    } else {
      parent.style.maxHeight = '43px';
      el.style.transform = 'rotate(0deg)';
    }
    ev.stopPropagation();
  };

  const renderOption = (option) => {
    const PlaceName = option.PlaceName || '';
    const FullAddressText = option.FullAddressText || '';
    const FullName = option.FullName || '';
    const placeExists = PlaceName !== '';
    const nameExists = FullName !== '';
    return (
      <Grid container alignItems="center">
        {renderOptionIcon(option)}
        <Grid
          item
          xs
          style={{
            overflow: 'hidden',
            position: 'relative',
            maxHeight: 43,
          }}>
          <span style={{ fontWeight: 400 }}>{FullAddressText}</span>
          <KeyboardArrowDownIcon style={chevronStyle} onClick={onOptionExpand} />
          <div className={classes.option}>
            {placeExists && <div>{PlaceName}</div>}
            {nameExists && <div>{FullName}</div>}
          </div>
        </Grid>
      </Grid>
    );
  };

  const getOptionSelected = (option, value) => {
    return (
      option.ptsLocationAddressID === value.ptsLocationAddressID ||
      option.ptsAddressID === value.ptsAddressID
    );
  };

  const onAdd = () => {
    props.showAddressDialog();
  };

  const helperText = compact ? undefined : typeof error === 'string' ? error : ' ';

  const startAdornment = addEnabled ? (
    <IconButton style={btnStyle} onClick={onAdd}>
      <AddIcon fontSize="small" />
    </IconButton>
  ) : undefined;

  return (
    <div className={clsx(classes.wrap, props.className)}>
      <Autocomplete
        getOptionLabel={getOptionLabel}
        options={autocompleteOptions}
        autoComplete
        includeInputInList
        filterSelectedOptions
        className={classes.autocomplete}
        style={props.style}
        value={value}
        onChange={onAddressSelect}
        onInputChange={onInputChange}
        renderOption={renderOption}
        getOptionSelected={getOptionSelected}
        filterOptions={(option) => option}
        renderInput={(params) => (
          <>
            <TextField
              {...params}
              error={!!error}
              helperText={helperText}
              label={label}
              variant="outlined"
              size="small"
              inputRef={inputRef}
              InputProps={{
                ...params.InputProps,
                autoComplete: 'new-password',
                startAdornment,
              }}
              inputProps={{
                ...params.inputProps,
                autoComplete: 'new-password',
              }}
            />
          </>
        )}
      />
    </div>
  );
}

AddressLookup.defaultProps = {
  ptsPlaces: false,
  ptsAddresses: true,
  googleAddresses: false,
};

const mapStateToProps = (state) => ({
  wsClient: state.websocket,
  map: state.map.map,
  dictionary: state.dictionary,
  dataUpdate: state.dataUpdate,
  dialogs: state.dialogs,
});

export default connect(mapStateToProps, {
  handleError,
  showAddressDialog,
})(AddressLookup);
